import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { executeAction } from '../../utils/actionExecutor';

/**
 * NavigationPreview - Main preview component for navigation
 * Renders different navigation types based on configuration
 */
export const NavigationPreview = ({ component, theme, onClick, isSelected }) => {
  const { commonAttrs, specificAttrs } = component;
  const {
    navigationType = 'horizontal-navbar',
    position = 'top-sticky',
    menuItems = [],
    backgroundColor = '#1F2937',
    textColor = '#FFFFFF',
    hoverColor = '#3B82F6'
  } = specificAttrs;

  // Container style with common attributes
  const containerStyle = {
    padding: `${commonAttrs?.paddingTop || 16}px ${commonAttrs?.paddingRight || 16}px ${commonAttrs?.paddingBottom || 16}px ${commonAttrs?.paddingLeft || 16}px`,
    margin: `${commonAttrs?.marginTop || 8}px ${commonAttrs?.marginRight || 0}px ${commonAttrs?.marginBottom || 8}px ${commonAttrs?.marginLeft || 0}px`,
    width: commonAttrs?.width === 'auto' ? 'auto' : `${commonAttrs?.width || 100}%`,
    cursor: onClick ? 'pointer' : 'default',
    border: isSelected ? '2px solid #3B82F6' : '2px solid transparent',
    borderRadius: '4px'
  };

  // Render based on navigation type
  switch (navigationType) {
    case 'horizontal-navbar':
      return (
        <div style={containerStyle} onClick={onClick}>
          <HorizontalNavbar 
            menuItems={menuItems}
            backgroundColor={backgroundColor}
            textColor={textColor}
            hoverColor={hoverColor}
            position={position}
          />
        </div>
      );
    
    case 'vertical-sidebar':
      return (
        <div style={containerStyle} onClick={onClick}>
          <VerticalSidebar 
            menuItems={menuItems}
            backgroundColor={backgroundColor}
            textColor={textColor}
            hoverColor={hoverColor}
          />
        </div>
      );
    
    case 'footer-navigation':
      return (
        <div style={containerStyle} onClick={onClick}>
          <FooterNav 
            menuItems={menuItems}
            backgroundColor={backgroundColor}
            textColor={textColor}
            hoverColor={hoverColor}
          />
        </div>
      );
    
    case 'mobile-bottom-bar':
      return (
        <div style={containerStyle} onClick={onClick}>
          <BottomBar 
            menuItems={menuItems}
            backgroundColor={backgroundColor}
            textColor={textColor}
            hoverColor={hoverColor}
          />
        </div>
      );
    
    default:
      return (
        <div style={containerStyle} onClick={onClick}>
          <HorizontalNavbar 
            menuItems={menuItems}
            backgroundColor={backgroundColor}
            textColor={textColor}
            hoverColor={hoverColor}
            position={position}
          />
        </div>
      );
  }
};

/**
 * HorizontalNavbar - Classic top navigation bar
 */
const HorizontalNavbar = ({ menuItems, backgroundColor, textColor, hoverColor, position }) => {
  const isSticky = position === 'top-sticky';
  const isFixed = position === 'top-fixed';

  return (
    <nav style={{ 
      display: 'flex',
      alignItems: 'center',
      backgroundColor,
      padding: '16px 24px',
      gap: '24px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: isSticky || isFixed ? 'sticky' : 'relative',
      top: isSticky || isFixed ? 0 : 'auto',
      zIndex: 100,
      width: '100%'
    }}>
      {menuItems.length === 0 ? (
        <span style={{ 
          fontSize: '13px', 
          color: textColor, 
          opacity: 0.6,
          fontStyle: 'italic'
        }}>
          Add menu items to see navigation
        </span>
      ) : (
        menuItems.map(item => (
          <MenuItem 
            key={item.id}
            item={item}
            textColor={textColor}
            hoverColor={hoverColor}
            layout="horizontal"
          />
        ))
      )}
    </nav>
  );
};

/**
 * VerticalSidebar - Side navigation menu
 */
const VerticalSidebar = ({ menuItems, backgroundColor, textColor, hoverColor }) => {
  return (
    <nav style={{ 
      display: 'flex',
      flexDirection: 'column',
      backgroundColor,
      padding: '24px 16px',
      gap: '8px',
      minHeight: '400px',
      width: '240px',
      boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
    }}>
      {menuItems.length === 0 ? (
        <span style={{ 
          fontSize: '13px', 
          color: textColor, 
          opacity: 0.6,
          fontStyle: 'italic'
        }}>
          Add menu items
        </span>
      ) : (
        menuItems.map(item => (
          <MenuItem 
            key={item.id}
            item={item}
            textColor={textColor}
            hoverColor={hoverColor}
            layout="vertical"
          />
        ))
      )}
    </nav>
  );
};

/**
 * FooterNav - Footer navigation links
 */
const FooterNav = ({ menuItems, backgroundColor, textColor, hoverColor }) => {
  return (
    <nav style={{ 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor,
      padding: '20px 24px',
      gap: '32px',
      boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
      width: '100%'
    }}>
      {menuItems.length === 0 ? (
        <span style={{ 
          fontSize: '13px', 
          color: textColor, 
          opacity: 0.6,
          fontStyle: 'italic'
        }}>
          Add footer links
        </span>
      ) : (
        menuItems.map(item => (
          <MenuItem 
            key={item.id}
            item={item}
            textColor={textColor}
            hoverColor={hoverColor}
            layout="horizontal"
            showIcon={false}
          />
        ))
      )}
    </nav>
  );
};

/**
 * BottomBar - Mobile-style bottom navigation
 */
const BottomBar = ({ menuItems, backgroundColor, textColor, hoverColor }) => {
  return (
    <nav style={{ 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-around',
      backgroundColor,
      padding: '12px 16px',
      boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
      width: '100%',
      position: 'relative'
    }}>
      {menuItems.length === 0 ? (
        <span style={{ 
          fontSize: '13px', 
          color: textColor, 
          opacity: 0.6,
          fontStyle: 'italic'
        }}>
          Add items
        </span>
      ) : (
        menuItems.slice(0, 5).map(item => (
          <MenuItem 
            key={item.id}
            item={item}
            textColor={textColor}
            hoverColor={hoverColor}
            layout="bottom-bar"
          />
        ))
      )}
      {menuItems.length > 5 && (
        <span style={{ 
          fontSize: '10px', 
          color: textColor, 
          opacity: 0.5,
          position: 'absolute',
          bottom: '2px',
          right: '8px'
        }}>
          +{menuItems.length - 5} more
        </span>
      )}
    </nav>
  );
};

/**
 * MenuItem - Reusable menu item component
 */
const MenuItem = ({ item, textColor, hoverColor, layout = 'horizontal', showIcon = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Get icon component
  const IconComponent = Icons[item.icon] || Icons.Circle;

  // Handle click - execute navigation (slot-based or action-based)
  const handleClick = async (e) => {
    e.stopPropagation(); // Prevent parent onClick
    
    // Check if new slot-based navigation is configured
    if (item.targetPage) {
      try {
        const targetPage = item.targetPage;
        const openInSlot = item.openInSlot || 'body';
        const openInNewTab = item.openInNewTab || false;
        
        console.log('🔗 Slot-based navigation:', { targetPage, openInSlot, openInNewTab });
        
        // If new tab, use traditional navigation
        if (openInNewTab) {
          const urlParams = new URLSearchParams(window.location.search);
          const appName = urlParams.get('app') || localStorage.getItem('currentAppName');
          if (appName) {
            const previewUrl = `/app-preview?app=${encodeURIComponent(appName)}&page=${encodeURIComponent(targetPage)}`;
            window.open(previewUrl, '_blank', 'noopener,noreferrer');
            return;
          }
        }
        
        // Slot-based navigation - dispatch custom event
        const clearSlot = item.clearSlotBeforeLoad !== false; // Default to true
        const eventDetail = {
          targetPage,
          targetSlotId: openInSlot, // Use actual slotId from layout
          targetSlot: openInSlot, // Also include targetSlot for compatibility
          clearSlot,
          sourceMenuItem: item
        };
        
        console.log('📤 [NavigationPreview] Dispatching slotNavigation event', eventDetail);
        const navigationEvent = new CustomEvent('slotNavigation', {
          detail: eventDetail,
          bubbles: true, // Allow event to bubble
          cancelable: true
        });
        
        const dispatched = window.dispatchEvent(navigationEvent);
        console.log('✅ [NavigationPreview] Slot navigation event dispatched', { 
          targetPage, 
          targetSlotId: openInSlot, 
          targetSlot: openInSlot,
          clearSlot,
          eventDispatched: dispatched
        });
        
        // Also log to help debug
        if (!dispatched) {
          console.warn('⚠️ [NavigationPreview] Event was prevented');
        }
      } catch (error) {
        console.error('❌ Slot navigation failed:', error);
      }
    }
    // Fallback to action system for backward compatibility
    else if (item.action) {
      try {
        console.log('🔗 Executing navigation action:', item.action);
        await executeAction(item.action);
      } catch (error) {
        console.error('❌ Action execution failed:', error);
      }
    } else {
      console.log('ℹ️ No navigation configured for:', item.label);
    }
  };

  // Layout-specific styles
  const getLayoutStyles = () => {
    switch (layout) {
      case 'vertical':
        return {
          flexDirection: 'row',
          justifyContent: 'flex-start',
          padding: '10px 12px',
          width: '100%',
          borderRadius: '6px',
          backgroundColor: isHovered ? `${hoverColor}20` : 'transparent'
        };
      
      case 'bottom-bar':
        return {
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '8px 12px',
          minWidth: '60px',
          borderRadius: '8px',
          backgroundColor: 'transparent'
        };
      
      case 'horizontal':
      default:
        return {
          flexDirection: 'row',
          justifyContent: 'center',
          padding: '8px 12px',
          borderRadius: '6px',
          backgroundColor: 'transparent'
        };
    }
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: layout === 'bottom-bar' ? '4px' : '8px',
        color: isHovered ? hoverColor : textColor,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: layout === 'bottom-bar' ? '11px' : '14px',
        fontWeight: isHovered ? '600' : '500',
        position: 'relative',
        transition: 'all 0.2s ease',
        ...getLayoutStyles()
      }}
    >
      {/* Icon */}
      {showIcon && IconComponent && (
        <IconComponent size={layout === 'bottom-bar' ? 20 : 18} />
      )}
      
      {/* Label */}
      <span style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {item.label}
      </span>
      
      {/* Badge */}
      {item.badge && (
        <span style={{
          backgroundColor: '#EF4444',
          color: '#FFFFFF',
          fontSize: layout === 'bottom-bar' ? '9px' : '10px',
          padding: layout === 'bottom-bar' ? '1px 4px' : '2px 6px',
          borderRadius: '10px',
          fontWeight: '600',
          position: layout === 'bottom-bar' ? 'absolute' : 'relative',
          top: layout === 'bottom-bar' ? '4px' : 'auto',
          right: layout === 'bottom-bar' ? '4px' : 'auto'
        }}>
          {item.badge}
        </span>
      )}
    </button>
  );
};

export default NavigationPreview;




